import { useState, useCallback, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import ErrorBoundary from "@/components/ErrorBoundary";
import SplashScreen from "@/components/SplashScreen";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import CreateKrew from "./pages/CreateKrew";
import KrewDetail from "./pages/KrewDetail";
import JoinKrew from "./pages/JoinKrew";
import Venues from "./pages/Venues";
import VenueDetail from "./pages/VenueDetail";
import Profile from "./pages/Profile";
import WalletPage from "./pages/WalletPage";
import Notifications from "./pages/Notifications";
import NotFound from "./pages/NotFound";
import VendorPortal from "./pages/VendorPortal";
import AdminDashboard from "./pages/AdminDashboard";
import Onboarding from "./pages/Onboarding";
import Leaderboard from "./pages/Leaderboard";
import CityLanding from "./pages/CityLanding";

const queryClient = new QueryClient();

const App = () => {
  // Only show splash once per browser session
  const [showSplash, setShowSplash] = useState(() => {
    if (typeof window === "undefined") return false;
    return !sessionStorage.getItem("krew_splash_seen");
  });
  const handleSplashComplete = useCallback(() => setShowSplash(false), []);
  useEffect(() => {
    if (!showSplash) return;
    sessionStorage.setItem("krew_splash_seen", "1");
  }, [showSplash]);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
              <BrowserRouter>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/join/:code" element={<JoinKrew />} />
                  <Route path="/city/:slug" element={<CityLanding />} />
                  <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                  <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/create-krew" element={<CreateKrew />} />
                    <Route path="/krew/:id" element={<KrewDetail />} />
                    <Route path="/venues" element={<Venues />} />
                    <Route path="/venue/:slug" element={<VenueDetail />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/wallet" element={<WalletPage />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/vendor" element={<VendorPortal />} />
                    <Route path="/admin" element={<AdminDashboard />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                  </Route>
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
